import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  LucideAngularModule,
  Upload,
  RefreshCw,
  X,
  Download,
  CheckCircle2,
  AlertTriangle,
  Undo2,
  Trash2,
} from 'lucide-angular';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import {
  AppReleasesService,
  AppReleaseDto,
  ReleaseUploadTarget,
} from '../../../core/services/app-releases.service';

/** Yuklash bosqichi — foydalanuvchi qaysi qadamda turganini ko'rib tursin. */
type PublishPhase = 'idle' | 'preparing' | 'uploading' | 'finishing';

@Component({
  selector: 'app-admin-app-releases',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, ConfirmDialogModule],
  providers: [ConfirmationService],
  templateUrl: './app-releases.component.html',
})
export class AdminAppReleasesComponent implements OnInit {
  private svc = inject(AppReleasesService);
  private confirmSvc = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);

  Upload = Upload;
  RefreshCw = RefreshCw;
  X = X;
  Download = Download;
  CheckCircle2 = CheckCircle2;
  AlertTriangle = AlertTriangle;
  Undo2 = Undo2;
  Trash2 = Trash2;

  releases = signal<AppReleaseDto[]>([]);
  loading = signal(false);
  submitting = signal(false);
  uploadProgress = signal(0);
  phase = signal<PublishPhase>('idle');
  error = signal<string | null>(null);

  dialogOpen = signal(false);
  selectedFiles = signal<File[]>([]);

  form = {
    version: '',
    channel: 'win',
    minSupportedVersion: '',
    isMandatory: false,
    releaseNotes: '',
  };

  ngOnInit() {
    this.load();
  }

  load() {
    this.loading.set(true);
    this.svc
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.releases.set(res.data ?? []);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  openCreate() {
    this.form = {
      version: '',
      channel: 'win',
      minSupportedVersion: '',
      isMandatory: false,
      releaseNotes: '',
    };
    this.selectedFiles.set([]);
    this.uploadProgress.set(0);
    this.phase.set('idle');
    this.error.set(null);
    this.dialogOpen.set(true);
  }

  closeDialog() {
    if (this.submitting()) return; // yuklash o'rtasida yopib yubormaymiz
    this.dialogOpen.set(false);
  }

  /**
   * Fayl oynasi tizim dialogida ko'p marta ochilishi mumkin (masalan foydalanuvchi
   * bir vaqtda faqat bitta faylni belgilay olsa) — shuning uchun har safar tanlangan
   * fayllar oldingi ro'yxatni EZMAYDI, balki unga qo'shiladi. Bir xil nomdagi fayl
   * qayta tanlansa, eskisi yangisi bilan almashtiriladi.
   */
  onFilesPicked(event: Event) {
    const input = event.target as HTMLInputElement;
    const picked = input.files ? Array.from(input.files) : [];

    if (picked.length > 0) {
      const merged = [...this.selectedFiles()];
      for (const file of picked) {
        const existingIndex = merged.findIndex(
          (f) => f.name.toLowerCase() === file.name.toLowerCase(),
        );
        if (existingIndex >= 0) {
          merged[existingIndex] = file;
        } else {
          merged.push(file);
        }
      }
      this.selectedFiles.set(merged);
    }

    input.value = ''; // xuddi shu faylni yana tanlash imkoniyati uchun
    this.error.set(null);
  }

  removeFile(name: string) {
    this.selectedFiles.set(this.selectedFiles().filter((f) => f.name !== name));
  }

  /** Manifest tanlangan fayllar ichida bormi — serverga bormasdan oldin ogohlantiramiz. */
  get manifestMissing(): boolean {
    const files = this.selectedFiles();
    if (files.length === 0) return false;
    const expected = `releases.${this.form.channel}.json`;
    return !files.some((f) => f.name.toLowerCase() === expected);
  }

  get totalSelectedSize(): number {
    return this.selectedFiles().reduce((sum, f) => sum + f.size, 0);
  }

  get canSubmit(): boolean {
    return (
      !this.submitting() &&
      this.form.version.trim().length > 0 &&
      this.selectedFiles().length > 0 &&
      !this.manifestMissing
    );
  }

  /**
   * Chiqarish uch qadamda bo'ladi:
   *   1. backenddan imzolangan manzillar olinadi (qoidalar shu yerda tekshiriladi)
   *   2. fayllar TO'G'RIDAN-TO'G'RI R2'ga qo'yiladi — api.miyzan.uz yo'lda yo'q,
   *      shuning uchun nginx va Cloudflare hajm chegaralari ta'sir qilmaydi
   *   3. backend fayllarni omborda tekshirib, chiqarilishni yozadi
   *
   * 2-qadam o'rtasida uzilsa 3-qadam bajarilmaydi: omborda yetim fayl qoladi,
   * lekin bazada qator YO'Q — ya'ni hech bir kassa buzuq yangilanishga tushmaydi.
   * Qayta urinilganda fayllar aynan o'sha nom bilan ustiga yoziladi.
   */
  async publish() {
    if (!this.canSubmit) return;

    const files = this.selectedFiles();
    const version = this.form.version.trim();
    const minSupportedVersion = this.form.minSupportedVersion.trim() || null;

    this.submitting.set(true);
    this.uploadProgress.set(0);
    this.phase.set('preparing');
    this.error.set(null);

    try {
      const urls = await firstValueFrom(
        this.svc.requestUploadUrls({
          version,
          channel: this.form.channel,
          minSupportedVersion,
          files: files.map((f) => f.name),
        }),
      );

      const targets = urls.data ?? [];
      if (targets.length === 0) throw new Error('Server yuklash manzillarini qaytarmadi');

      this.phase.set('uploading');
      await this.uploadAll(targets, files);

      this.phase.set('finishing');
      await firstValueFrom(
        this.svc.confirm({
          version,
          channel: this.form.channel,
          minSupportedVersion,
          isMandatory: this.form.isMandatory,
          releaseNotes: this.form.releaseNotes.trim() || null,
          files: files.map((f) => f.name),
        }),
      );

      this.dialogOpen.set(false);
      this.load();
    } catch (err) {
      this.error.set(this.describeError(err));
    } finally {
      this.submitting.set(false);
      this.phase.set('idle');
    }
  }

  /**
   * Fayllarni ketma-ket yuklaydi. Progress BAYT bo'yicha hisoblanadi, fayllar
   * soni bo'yicha emas — aks holda 200 MB lik paket 1 KB lik manifest bilan
   * teng ulush olib, ko'rsatkich yolg'on gapirardi.
   */
  private async uploadAll(targets: ReleaseUploadTarget[], files: File[]): Promise<void> {
    const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
    let doneBytes = 0;

    for (const target of targets) {
      const file = files.find((f) => f.name === target.fileName);
      if (!file) throw new Error(`'${target.fileName}' fayli tanlanganlar orasida topilmadi`);

      await new Promise<void>((resolve, reject) => {
        this.svc.uploadToStorage(target, file).subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress) {
              const sent = doneBytes + event.loaded;
              this.uploadProgress.set(Math.min(100, Math.round((sent / totalBytes) * 100)));
            }
          },
          error: reject,
          complete: () => {
            doneBytes += file.size;
            this.uploadProgress.set(Math.min(100, Math.round((doneBytes / totalBytes) * 100)));
            resolve();
          },
        });
      });
    }
  }

  private describeError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      // status 0 — javob umuman kelmadi: R2 CORS sozlanmagan yoki tarmoq uzildi.
      // Bu eng ehtimolli birinchi xato, shuning uchun alohida tushuntiramiz.
      if (err.status === 0)
        return "Omborga ulanib bo'lmadi. R2 bucket'ida CORS sozlanganini va internet uzilmaganini tekshiring.";

      const body = err.error as { errors?: string[]; error?: string } | string | null;
      if (typeof body === 'string' && body.trim()) return body;
      if (body && typeof body === 'object')
        return body.errors?.[0] ?? body.error ?? `Xatolik (${err.status})`;

      return `Xatolik (${err.status})`;
    }

    return err instanceof Error ? err.message : "Yuklab bo'lmadi";
  }

  get phaseLabel(): string {
    switch (this.phase()) {
      case 'preparing':
        return 'Tayyorlanmoqda…';
      case 'uploading':
        return `Yuklanmoqda… ${this.uploadProgress()}%`;
      case 'finishing':
        return 'Tasdiqlanmoqda…';
      default:
        return '';
    }
  }

  toggleActive(release: AppReleaseDto) {
    this.svc
      .setActive(release.id, !release.isActive)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.load() });
  }

  /**
   * Chiqarilishni butunlay o'chirish — qator ham, R2'dagi paketlar ham ketadi.
   *
   * Oxirgi (faol) versiyani o'chirish ALOHIDA ogohlantiriladi: R2'dagi manifest
   * (`releases.win.json`) o'sha oxirgi nashrdan qolgan bo'ladi va o'chirilgan
   * paketga ishora qilib turaveradi — kassalar yangilanishni topib, yuklab
   * ololmaydi. Bunday holatda yangi versiyani qaytadan yuklash kerak.
   */
  confirmDelete(release: AppReleaseDto) {
    const warning = release.isLatest
      ? `\n\nDIQQAT: bu — mijozlarga berilayotgan OXIRGI versiya. O'chirilgandan keyin ` +
        `R2'dagi manifest yo'q paketga ishora qilib qoladi va yangilanish ishlamaydi. ` +
        `Darhol yangi versiya yuklashingiz kerak bo'ladi.`
      : '';

    this.confirmSvc.confirm({
      header: "Chiqarilishni o'chirish",
      icon: 'pi pi-exclamation-triangle',
      message:
        `${release.version} (${release.channel}) butunlay o'chirilsinmi? ` +
        `Paketlar R2 omboridan ham o'chiriladi va buni qaytarib bo'lmaydi. ` +
        `Vaqtinchalik to'xtatish uchun "Qaytarib olish" tugmasi bor.${warning}`,
      acceptLabel: "Ha, o'chirish",
      rejectLabel: 'Bekor',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteRelease(release),
    });
  }

  private deleteRelease(release: AppReleaseDto) {
    this.error.set(null);
    this.svc
      .delete(release.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          // Baham ko'riladigan fayllar (manifest, Setup.exe) omborda qoladi.
          // "O'chirildi" deb aytib, fayl qolib ketgani keyin tushunarsiz bo'lardi.
          const kept = res.data?.keptFiles ?? [];
          if (kept.length) {
            this.error.set(
              `Chiqarilish o'chirildi. Bu fayllar boshqa chiqarilishlar ham ishlatgani ` +
                `uchun omborda qoldirildi: ${kept.join(', ')}`,
            );
          }
          this.load();
        },
        error: (err: HttpErrorResponse) => {
          this.error.set(err.error?.errors?.[0] ?? err.error?.message ?? "O'chirib bo'lmadi");
        },
      });
  }

  formatSize(bytes: number): string {
    if (bytes <= 0) return '—';
    const mb = bytes / (1024 * 1024);
    return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
  }
}
